import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faUser } from '@fortawesome/pro-light-svg-icons';
import { faChevronDown, faChevronUp, faCircle, faPlay } from '@fortawesome/pro-solid-svg-icons';
import { ExecutorModalComponent } from '../executor-modal/executor-modal.component';
import { TypeChangeEnum } from '../../core/enums/type-change.enum';
import { PresentationTypeEnum } from '../../core/enums/presentation-type.enum';
import { DeclarationsService } from '../../core/services/declarations/declarations.service';
import { AuthService } from '../../core/services/auth.service';
import { CommentModalComponent } from '../comment-modal/comment-modal.component';
import { CheckboxModule } from 'primeng/checkbox';
import { DropdownModule } from 'primeng/dropdown';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';



@Component({
  selector: 'app-declaration-phases',
  standalone: true,
  imports: [FontAwesomeModule, CommonModule, FormsModule, ExecutorModalComponent, CommentModalComponent, CheckboxModule, DropdownModule, InputTextModule, ProgressSpinnerModule],
  templateUrl: './declaration-phases.component.html',
  styleUrl: './declaration-phases.component.scss'
})
export class DeclarationPhasesComponent implements OnChanges {
  @Input() phases: any | undefined;
  @Input() isMultiple: number;
  @Input() format: string;
  @Input() isBlocked: boolean;
  @Input() isDynamic: boolean;
  @Input() isAccountingModel: boolean;
  @Input() isLaborModel: boolean;
  @Input() bankAccount: string | undefined;
  @Output() commentEvent = new EventEmitter<any>();
  @Output() changesEvent = new EventEmitter<any>();
  @Output() closeEvent = new EventEmitter<any>();
  @Output() taskSelected = new EventEmitter<any>();

  @Output() disableBankAccountAndPresentation = new EventEmitter<any>();
  @Output() bankAccountConfirmationStatusChange = new EventEmitter<boolean>();

  @ViewChild('modal') modal: ExecutorModalComponent;
  @ViewChild('commentModal') commentModal: CommentModalComponent;

  faChevronDown = faChevronDown;
  faChevronUp = faChevronUp;
  faUser = faUser;
  faPlay = faPlay;
  faCircle = faCircle;

  isSaving = false;

  changes = [];
  changedTasks = [];
  modalData = [];

  isAccordionOpen = false;
  hoveringExecutor = null;
  hoverTask = null;
  hoverPhase = null;

  selectedTask = null;
  selectedPhase = null;
  selectedTasks: any[] = [];
  isMultiSelectMode = false;

  allExpanded = false;

  configExecutorModal: any = undefined;
  showExecutorModal: boolean = false;

  configCommentModal: any = undefined;
  showCommentModal: boolean = false;
  user: any;

  constructor(
    private declarationService: DeclarationsService,
    private authService: AuthService,
  ) { }

  ngOnInit() {
    this.user = this.authService.getUser()?.user;
    this.checkPhasesStatus();
    this.formatInitialValues();
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (changes['phases'] && this.phases) {
      this.formatInitialValues();
      // Aplicar todos los autofills en carga/recarga (igual que al cambiar tipo de presentación)
      this.runAllAutoFills();
      if (this.changedTasks.length > 0 && this.isMultiple === 1) {
        this.isSaving = true;
        try {
          if (this.changedTasks.length > 1) {
            await this.declarationService.massTaskStatusUpdate(this.changedTasks);
          } else {
            await this.declarationService.taskStatusUpdate(this.changedTasks[0]);
          }
          this.syncInitialValues();
          this.changedTasks = [];
        } finally {
          this.isSaving = false;
        }
        this.checkBankAccountConfirmation();
      }
      this.checkPhasesStatus();
    }
  }

  phaseState(phase: number) {
    let state = 0;
    let disabled = 0;
    if (this.phases[phase] && this.phases[phase].tasks.length > 0) {

      for (let task of this.phases[phase].tasks) {
        if (task.value && task.isDisabled === false) {
          state = state + 1;
        }
        if (task.isDisabled === true) {
          disabled = disabled + 1;
        }
      }
      if (state === this.phases[phase].tasks.length - disabled && state > 0) {
        return ["Finalizada", "#5cbc9c"];
      } else if (state < this.phases[phase].tasks.length - disabled && state > 0) {
        return ["En curso", "#ff9100"];
      } else if (state === this.phases[phase].tasks.length - disabled && state === 0) {
        return ["Bloqueada", "#cecece"];
      } else {
        return ["Pendiente", "#757575"];
      }
    } else {
      return "#cecece";
    }
  }

  checkPhasesStatus() {
    let disableBankAccount = false;
    let disablePresentation = false;

    // Verificar si alguna fase que contiene "Cuenta bancaria" o "Tipo presentación - fichero" está finalizada → deshabilitar cuenta y presentación
    // Para modelos isDynamic: si la fase 5 está completada → solo deshabilitar tipo de presentación
    for (const phase of this.phases) {
      let state = 0;
      let disabled = 0;

      for (const task of phase.tasks) {
        if (task.value && task.isDisabled === false) {
          state = state + 1;
        }
        if (task.isDisabled === true) {
          disabled = disabled + 1;
        }
      }

      const isPhaseCompleted = state === phase.tasks.length - disabled && state > 0;
      if (isPhaseCompleted) {
        if (phase.tasks.some(task => task.name === "Cuenta bancaria" || task.name === "Tipo presentación - fichero")) {
          disableBankAccount = true;
          disablePresentation = true;
        }
        if (this.isDynamic && Number(phase.phase) === 5) {
          disablePresentation = true;
        }
      }
    }

    // Emitir en el siguiente ciclo de detección para evitar NG0100
    Promise.resolve().then(() => {
      this.disableBankAccountAndPresentation.emit({ disableBankAccount, disablePresentation });
    });
  }


  showFase(index) {
    this.phases[index].show = !this.phases[index].show;
  }

  isString(value) {
    return typeof value === 'string';
  }


  async selectDeselectPhases(task, phase, last) {
    try {
      await this.applySelectDeselectPhases(task, phase, last);
    } catch (error) {
      this.isSaving = false; // un error HTTP no debe dejar el overlay de guardado puesto
      throw error;
    }
  }

  private async applySelectDeselectPhases(task, phase, last) {
    this.isSaving = true;
    let startDeselecting = false;
    let startSelecting = true;
    if (!task.value) {
      for (let p of this.phases) {
        for (let t of p.tasks) {
          if (startDeselecting) {
            if (t.value || t.value === 0) {
              t.value = false; // Update UI
              if (t.ids) {
                t.ids.forEach(id => {
                  this.changedTasks.push({ id: id, type: TypeChangeEnum.StatusTask, change: null });
                  if (this.isAccountingModel && (p.phase === 2 || p.phase === 3)) {
                    t.executor = null;
                    this.changedTasks.push({ id: id, type: TypeChangeEnum.Executor, change: null });
                  }
                });
              } else {
                this.changedTasks.push({ id: t.id, type: TypeChangeEnum.StatusTask, change: null });
                if (this.isAccountingModel && (p.phase === 2 || p.phase === 3)) {
                  t.executor = null;
                  this.changedTasks.push({ id: t.id, type: TypeChangeEnum.Executor, change: null });
                }

              }
              this.modalData.push({ task: t.name, phase: p.phase });
            }
          }

          if (p === phase && t === task) {
            // Checkbox already set task.value to false, but we need to record it
            if (t.ids) {
              t.ids.forEach(id => {
                this.changedTasks.push({ id: id, type: TypeChangeEnum.StatusTask, change: null });
                if (this.isAccountingModel && (p.phase === 2 || p.phase === 3)) {
                  t.executor = null;
                  this.changedTasks.push({ id: id, type: TypeChangeEnum.Executor, change: null });
                }
              });
            } else {
              this.changedTasks.push({ id: t.id, type: TypeChangeEnum.StatusTask, change: null });
              if (this.isAccountingModel && (p.phase === 2 || p.phase === 3)) {
                t.executor = null;
                this.changedTasks.push({ id: t.id, type: TypeChangeEnum.Executor, change: null });
              }
            }
            this.modalData.push({ task: t.name, phase: p.phase });
            if (task.type === 'OPTIONS') {
              this.taskSelected.emit();
            }
            startDeselecting = true;
          }
        }
      }
    }
    else {
      for (let p of this.phases) {

        for (let t of p.tasks) {
          if (!t.value) {
            if (startSelecting) {
              if (t.type === 'CHECK') {
                if (t.isDisabled === false) {
                  t.value = true;
                  if (t.ids) {
                    t.ids.forEach(id => {
                      this.changedTasks.push({ id: id, type: TypeChangeEnum.StatusTask, change: true });
                      t.executor = this.user.name;
                      this.changedTasks.push({ id: id, type: TypeChangeEnum.Executor, change: this.user.id });

                    });
                  } else {
                    this.changedTasks.push({ id: t.id, type: TypeChangeEnum.StatusTask, change: true });
                    t.executor = this.user.name;
                    this.changedTasks.push({ id: t.id, type: TypeChangeEnum.Executor, change: this.user.id });
                  }
                }
              } else {
                if (t.isDisabled === false) {
                  t.value = '0';
                  if (t.ids) {
                    t.ids.forEach(id => {
                      this.changedTasks.push({ id: id, type: TypeChangeEnum.StatusTask, change: '0' });
                      t.executor = this.user.name;
                      this.changedTasks.push({ id: id, type: TypeChangeEnum.Executor, change: this.user.id });

                    });
                  } else {
                    this.changedTasks.push({ id: t.id, type: TypeChangeEnum.StatusTask, change: '0' });
                    t.executor = this.user.name;
                    this.changedTasks.push({ id: t.id, type: TypeChangeEnum.Executor, change: this.user.id });
                  }
                }
              }

            }
          }
          if (p === phase && t === task) {
            const index = p.tasks.findIndex(task => task === t);
            const nextT = p.tasks[index + 1];
            if (t.ids) {
              t.ids.forEach(id => {
                this.changedTasks.push({ id: id, type: TypeChangeEnum.StatusTask, change: t.value });
                t.executor = this.user.name;
                this.changedTasks.push({ id: id, type: TypeChangeEnum.Executor, change: this.user.id });
              });
            } else {
              this.changedTasks.push({ id: t.id, type: TypeChangeEnum.StatusTask, change: t.value });
              t.executor = this.user.name;
              this.changedTasks.push({ id: t.id, type: TypeChangeEnum.Executor, change: this.user.id });
            }
            if (task.type === 'OPTIONS') {
              this.taskSelected.emit();
            }
            startSelecting = false;

          }

        }
      }
    }

    if (task.value && task.name === 'Importe declaración') {
      this.autoFillAplazamientoImportes(task);
    }

    if (task.value && task.name === 'Importe tras compensar') {
      this.autoFillCompensarImportes(task);
    }

    if (task.value && (task.name === 'Importe declaración' || task.name === 'Importe primer plazo NRC' || task.name === 'Importe tras compensar' || task.name === 'Forma de presentación')) {
      this.autoFillAplazamientoParcial(true);
    }

    const anyInitiallyFinished = this.phases.some(p =>
      p.tasks.some(t => {
        const changed = this.changedTasks.find(ct => this.isSameTask(ct, t));
        return changed && (changed.change === null || changed.change === false) && (t.initialValue || t.initialValue === 0);
      })
    );

    if (this.isMultiple > 1) {
      if (anyInitiallyFinished) {
        this.openCommentModal();
      } else {
        this.changedTasks.forEach(task => {
          this.changes.push(task);
          this.sendChanges();
        });
        this.syncInitialValues();
        this.changedTasks = [];
        this.modalData = [];
      }
      this.isSaving = false;
    } else {
      if (anyInitiallyFinished) {
        this.openCommentModal();
      } else {
        if (this.changedTasks.length > 1) {
          await this.declarationService.massTaskStatusUpdate(this.changedTasks);
        } else if (this.changedTasks.length === 1) {
          await this.declarationService.taskStatusUpdate(this.changedTasks[0]);
        }
        this.syncInitialValues();
        this.changedTasks = [];
        this.modalData = [];
      }
      this.isSaving = false;
      this.checkPhasesStatus();
      this.checkBankAccountConfirmation();
    }
  }

  syncInitialValues() {
    this.phases.forEach(p => {
      p.tasks.forEach(t => {
        const changed = this.changedTasks.find(ct => this.isSameTask(ct, t));
        if (changed) {
          t.initialValue = t.value;
        }
      });
    });
  }

  getActiveTasks(tasks) {
    return tasks.filter(task => !task.isDisabled);
  }

  hasIncompleteTasks(phase): boolean {
    const activeTasks = this.getActiveTasks(phase.tasks);
    return activeTasks.some(task => !task.value && task.value !== 0);
  }



  openExecutorModal(task): void {
    // No permitir cambiar ejecutor de tareas completadas
    if (task.value || task.value === 0) {
      return;
    }
    this.selectedTask = task;
    this.selectedPhase = null;

    this.showExecutorModal = true;
  }

  openPhaseExecutorModal(phase, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    // Verificar que haya tareas no completadas en la fase
    const incompleteTasks = phase.tasks.filter(task => !task.isDisabled && !task.value && task.value !== 0);
    if (incompleteTasks.length === 0) {
      return; // No hay tareas incompletas, no abrir el modal
    }
    this.selectedTask = null;
    this.selectedPhase = phase;

    this.showExecutorModal = true;
  }

  closeExecutorModal() {
    this.showExecutorModal = false;
    this.selectedTask = null;
    this.selectedPhase = null;
    if (this.selectedTasks.length > 0) {
      this.clearTaskSelection();
      this.isMultiSelectMode = false;
    }
  }

  async onExecutorSelect(executor): Promise<void> {
    if (this.selectedTasks.length > 0) {
      // Cambiar ejecutor de múltiples tareas seleccionadas
      if (this.isMultiple > 1) {
        this.selectedTasks.forEach(task => {
          task.executor = executor.name;
          if (task.ids) {
            task.ids.forEach(id => {
              this.changes.push({ id: id, type: TypeChangeEnum.Executor, change: executor.id });
            });
          } else {
            this.changes.push({ id: task.id, type: TypeChangeEnum.Executor, change: executor.id });
          }
        });
        this.sendChanges();
      } else {
        const updatePromises = this.selectedTasks.map(task => {
          task.executor = executor.name;
          if (task.ids) {
            return Promise.all(task.ids.map(id =>
              this.declarationService.taskExecutorUpdate(id, executor.id)
            ));
          } else {
            return this.declarationService.taskExecutorUpdate(task.id, executor.id);
          }
        });
        await Promise.all(updatePromises);
      }
      this.clearTaskSelection();
      this.isMultiSelectMode = false;
    } else if (this.selectedPhase) {
      // Cambiar ejecutor de todas las tareas de la fase (solo las no completadas)
      const phaseTasks = this.selectedPhase.tasks.filter(task => !task.isDisabled && !task.value && task.value !== 0);

      if (this.isMultiple > 1) {
        phaseTasks.forEach(task => {
          task.executor = executor.name;
          if (task.ids) {
            task.ids.forEach(id => {
              this.changes.push({ id: id, type: TypeChangeEnum.Executor, change: executor.id });
            });
          } else {
            this.changes.push({ id: task.id, type: TypeChangeEnum.Executor, change: executor.id });
          }
        });
        this.sendChanges();
      } else {
        const updatePromises = phaseTasks.map(task => {
          task.executor = executor.name;
          if (task.ids) {
            return Promise.all(task.ids.map(id =>
              this.declarationService.taskExecutorUpdate(id, executor.id)
            ));
          } else {
            return this.declarationService.taskExecutorUpdate(task.id, executor.id);
          }
        });
        await Promise.all(updatePromises);
      }
    } else if (this.selectedTask) {
      // Cambiar ejecutor de una tarea individual (solo si no está completada)
      if (this.selectedTask.value || this.selectedTask.value === 0) {
        return; // No permitir cambiar ejecutor de tareas completadas
      }
      this.selectedTask.executor = executor.name;
      if (this.isMultiple > 1) {
        for (let id of this.selectedTask.ids) {
          this.changes.push({ id: id, type: TypeChangeEnum.Executor, change: executor.id });
        }
        this.sendChanges();
      } else {
        await this.declarationService.taskExecutorUpdate(this.selectedTask.id, executor.id);
      }
    }
  }

  openCommentModal(): void {
    this.showCommentModal = true;
  }

  closeCommentModal() {
    this.phases.forEach(phase => {
      phase.tasks.forEach(task => {
        if (this.changedTasks.some(ct => this.isSameTask(ct, task))) {
          task.value = task.type === 'CHECK' ? true : '0';
        }
      });
    });
    this.closeEvent.emit();
    this.isSaving = false;
    this.showCommentModal = false;
    this.changedTasks = [];
    this.modalData = [];
  }

  async comment(comment) {
    if (this.isMultiple > 1) {
      this.changedTasks.forEach(task => {
        this.changes.push(task);
      });
      this.sendChanges();
    } else {
      await this.declarationService.massTaskStatusUpdate(this.changedTasks);
    }
    this.syncInitialValues();
    this.commentEvent.emit(comment);
    this.isSaving = false;
    this.showCommentModal = false;
    this.changedTasks = [];
    this.modalData = [];
  }

  getInitials(name: string) {
    if (name) {
      return name.split(/\s+/).filter(Boolean).map(word => word[0].toUpperCase()).join('');
    }
    else {
      return 'N/A';
    }
  }

  sendChanges() {
    this.changesEvent.emit(this.changes);
  }

  isPointerDisabled(index: number): boolean {
    if (this.isBlocked === true) {
      return true;
    }
    if (index === 0) {
      return false;
    }
    const previousPhaseState = this.phaseState(index - 1)[0];
    const currentPhaseState = this.phaseState(index)[0];


    if (previousPhaseState === 'Bloqueada') {
      if (this.phaseState(index - 2)[0] === 'Finalizada') {
        return false;
      } else {
        return true;
      }
    }
    if (previousPhaseState !== 'Finalizada') {
      return true;
    }
    if (currentPhaseState === 'Bloqueada') {
      return true;
    }

    return false;
  }

  getNgStylePointer(index: number): { [key: string]: string } {
    return this.isPointerDisabled(index) ? { 'pointer-events': 'none' } : {};
  }

  isAllPhaseDisabled(index) {
    let disabled = 0;
    for (let task of this.phases[index].tasks) {
      if (task.isDisabled === true) {
        disabled = disabled + 1;
      }
    }
    return disabled === this.phases[index].tasks.length;
  }

  isTaskBlocked(phase, task) {
    let phaseHasInputs = false;
    let index = 1
    this.phases[phase].tasks.forEach(t => {
      if (t.type !== 'CHECK') {
        phaseHasInputs = true;
      }
    });
    if (phaseHasInputs) {
      while (task > 0 && this.phases[phase].tasks[task - index]?.isDisabled) {
        index = index + 1;
      }
      if (task > 0 && !this.phases[phase].tasks[task - index]?.value && this.phases[phase].tasks[task - index]?.isDisabled) {
        return true;
      }
      else {
        return false;
      }
    }
    else {
      return false;
    }
  }

  isBankAccountCheckboxDisabled(task: any, phaseIndex: number): boolean {
    if (phaseIndex === 0 && task.name === "Cuenta bancaria" && !this.isLaborModel) {
      if (!this.bankAccount || this.bankAccount.trim() === '') {
        return true;
      }
    }
    return false;
  }

  isBankAccountConfirmationCompleted(): boolean {
    // Buscar la tarea "Confirmación cuenta bancaria" en todas las fases
    if (!this.phases) {
      return false;
    }

    for (let phase of this.phases) {
      for (let task of phase.tasks) {
        if (task.name === "Confirmación cuenta bancaria" && task.value && !task.isDisabled) {
          return true;
        }
      }
    }
    return false;
  }

  toggleMultiSelectMode(): void {
    this.isMultiSelectMode = !this.isMultiSelectMode;
    if (!this.isMultiSelectMode) {
      this.clearTaskSelection();
    }
  }

  toggleTaskSelection(task: any): void {
    // No permitir seleccionar tareas completadas o deshabilitadas
    if (task.value || task.value === 0 || task.isDisabled) {
      return;
    }

    const index = this.selectedTasks.findIndex(t => this.isSameTask(t, task));

    if (index > -1) {
      this.selectedTasks.splice(index, 1);
    } else {
      this.selectedTasks.push(task);
    }
  }

  isTaskSelected(task: any): boolean {
    return this.selectedTasks.some(t => this.isSameTask(t, task));
  }

  private isSameTask(task1: any, task2: any): boolean {
    // Si ambas tienen ids (modo múltiple), comparar el primer id
    if (task1.ids && task2.ids) {
      return task1.ids[0] === task2.ids[0];
    }
    // Si ambas tienen id (modo individual), comparar por id
    if (task1.id && task2.id) {
      return task1.id === task2.id;
    }
    // Si una tiene ids y otra id, comparar el primer ids con el id
    if (task1.ids && task2.id) {
      return task1.ids[0] === task2.id;
    }
    if (task1.id && task2.ids) {
      return task1.id === task2.ids[0];
    }
    return false;
  }

  clearTaskSelection(): void {
    this.selectedTasks = [];
  }

  openMultipleExecutorModal(): void {
    if (this.selectedTasks.length === 0) {
      return;
    }
    this.selectedTask = null;
    this.selectedPhase = null;
    this.showExecutorModal = true;
  }

  checkBankAccountConfirmation(): void {
    const isCompleted = this.isBankAccountConfirmationCompleted();
    Promise.resolve().then(() => {
      this.bankAccountConfirmationStatusChange.emit(isCompleted);
    });
  }

  isImporteDeclaracionDisabled(task: any): boolean {
    // Si la tarea no es "Importe declaración", no aplicar el bloqueo
    if (task.name !== "Importe declaración") {
      return false;
    }

    // Buscar la tarea "Confirmación importe con cliente" en todas las fases
    // Si está completada (tiene valor y no está deshabilitada), bloquear el input
    if (!this.phases) {
      return false;
    }

    for (let phase of this.phases) {
      for (let t of phase.tasks) {
        if (t.name === "Confirmación importe con cliente" && t.value && !t.isDisabled) {
          return true;
        }
      }
    }

    return false;
  }

  /** Parsea string numérico aceptando formato EU (1.234,56) y US (1,234.56 o 55.43) */
  private parseLocaleNumber(value: any): number | null {
    if (value == null || value === '') return null;
    const str = value.toString().trim();
    const lastComma = str.lastIndexOf(',');
    const lastDot = str.lastIndexOf('.');
    let clean: string;
    if (lastComma !== -1 && lastDot !== -1) {
      clean = lastComma > lastDot ? str.replace(/\./g, '').replace(',', '.') : str.replace(/,/g, '');
    } else if (lastComma !== -1) {
      const after = str.slice(lastComma + 1);
      clean = /^\d{1,2}$/.test(after) ? str.replace(',', '.') : str.replace(/,/g, '');
    } else if (lastDot !== -1) {
      const after = str.slice(lastDot + 1);
      clean = /^\d{1,2}$/.test(after) ? str : str.replace(/\./g, '');
    } else {
      clean = str;
    }
    const num = parseFloat(clean);
    return isNaN(num) ? null : num;
  }

  // Formatea un número con separador de miles (punto) - formato español
  formatNumber(value: any): string {
    if (value === null || value === undefined || value === '') return '';
    const num = this.parseLocaleNumber(value);
    if (num === null) return value.toString();
    return num.toLocaleString('es-ES', {
      useGrouping: true,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });
  }

  // Parsea un número formateado y devuelve el valor sin formato
  parseNumber(event: any, task: any): void {
    const inputValue = event.target.value;
    // Eliminar puntos (separadores de miles)
    const cleanValue = inputValue.replace(/\./g, '');
    task.value = cleanValue;
  }

  // Formatea el valor cuando el input pierde el foco (máx 2 decimales)
  onNumberBlur(event: any, task: any): void {
    if (task.value || task.value === 0) {
      const num = this.parseTaskNumber(task.value);
      const rounded = num !== null ? Math.round(num * 100) / 100 : null;
      task.value = rounded !== null ? this.formatNumber(rounded) : '';
    }
  }

  // Desformatea el valor cuando el input recibe el foco (quita miles, mantiene decimales)
  onNumberFocus(event: any, task: any): void {
    if (task.value || task.value === 0) {
      const num = this.parseLocaleNumber(task.value);
      if (num !== null) {
        task.value = num.toString().replace('.', ',');
      }
    }
  }

  private autoFillAplazamientoImportes(importeTask: any): void {
    if (this.format !== PresentationTypeEnum.PercentageDeferralPDF) return;
    this.distributeImporte60_40(importeTask);
  }

  private autoFillCompensarImportes(importeTask: any): void {
    if (this.format !== PresentationTypeEnum.ToCompensatePDF) return;
    this.distributeImporte60_40(importeTask);
  }

  private distributeImporte60_40(importeTask: any): void {
    const amount = this.parseLocaleNumber(importeTask.value);
    if (amount === null) return;

    const primerPlazo = Math.round(amount * 0.6 * 100) / 100;
    const segundoPlazo = Math.round(amount * 0.4 * 100) / 100;

    const targets = [
      { name: 'Importe primer plazo', amount: primerPlazo },
      { name: 'Importe segundo plazo', amount: segundoPlazo },
    ];

    for (const phase of this.phases) {
      for (const t of phase.tasks) {
        const target = targets.find(x => x.name === t.name);
        if (!target) continue;

        const formatted = this.formatNumber(target.amount);
        t.value = formatted;
        t.executor = this.user.name;

        const ids = t.ids ? t.ids : (t.id ? [t.id] : []);
        ids.forEach(id => {
          this.changedTasks.push({ id, type: TypeChangeEnum.StatusTask, change: formatted });
          this.changedTasks.push({ id, type: TypeChangeEnum.Executor, change: this.user.id });
        });
      }
    }
  }

  private autoFillAplazamientoParcial(persist = true): void {
    if (!this.phases) return;

    const isACompensarPDF = this.format === PresentationTypeEnum.ToCompensatePDF;
    const activeTasks = this.getAllActiveTasks();

    if (this.format === PresentationTypeEnum.PartialDeferralPDF) {
      // Aplazamiento parcial - PDF: usa Importe declaración (solo en tareas activas del flujo actual)
      this.autoFillAplazamientoParcialWithBase('Importe declaración', activeTasks, persist);
    } else if (isACompensarPDF) {
      // A compensar - PDF: solo si Forma de presentación = Aplazamiento parcial, usa Importe tras compensar
      const hasAplazamientoParcial = activeTasks.some(t => t.name === 'Forma de presentación' && t.value === 'Aplazamiento parcial');
      if (hasAplazamientoParcial) {
        this.autoFillAplazamientoParcialWithBase('Importe tras compensar', activeTasks, persist);
      }
    }
  }

  private parseTaskNumber(value: any): number | null {
    return this.parseLocaleNumber(value);
  }

  private autoFillAplazamientoParcialWithBase(baseTaskName: string, tasksScope?: any[], persist = true): void {
    const tasks = tasksScope ?? this.phases.flatMap(p => p.tasks);
    const importeTask = tasks.find(t => t.name === baseTaskName && t.value);
    const nrcTask = tasks.find(t => t.name === 'Importe primer plazo NRC' && t.value);
    const restanteTasks = tasks.filter(t => t.name === 'Importe restante a aplazar');

    if (restanteTasks.length === 0 || !importeTask || !nrcTask) return;

    const baseAmount = this.parseTaskNumber(importeTask.value);
    const importePrimerPlazoNRC = this.parseTaskNumber(nrcTask.value);
    if (baseAmount === null || importePrimerPlazoNRC === null || isNaN(baseAmount) || isNaN(importePrimerPlazoNRC)) return;
    const restante = Math.round((baseAmount - importePrimerPlazoNRC) * 100) / 100;
    const formatted = this.formatNumber(restante);

    restanteTasks.forEach(restanteTask => {
      restanteTask.value = formatted;
      restanteTask.executor = this.user.name;

      if (persist) {
        const ids = restanteTask.ids ? restanteTask.ids : (restanteTask.id ? [restanteTask.id] : []);
        ids.forEach(id => {
          this.changedTasks.push({ id, type: TypeChangeEnum.StatusTask, change: formatted });
          this.changedTasks.push({ id, type: TypeChangeEnum.Executor, change: this.user.id });
        });
      }
    });
  }

  /** Todas las tareas activas (no deshabilitadas) de todas las fases. */
  private getAllActiveTasks(): any[] {
    return this.phases ? this.phases.flatMap(p => p.tasks).filter(t => !t.isDisabled) : [];
  }

  /** Ejecuta todos los autofills (60-40, compensar, aplazamiento parcial) según formato y tareas con valor. */
  private runAllAutoFills(): void {
    if (!this.phases) return;
    const activeTasks = this.getAllActiveTasks();
    const importeDeclaracion = activeTasks.find(t => t.name === 'Importe declaración' && t.value);
    const importeTrasCompensar = activeTasks.find(t => t.name === 'Importe tras compensar' && t.value);
    if (importeDeclaracion) this.autoFillAplazamientoImportes(importeDeclaracion);
    if (importeTrasCompensar) this.autoFillCompensarImportes(importeTrasCompensar);
    this.autoFillAplazamientoParcial(true);
  }

  /**
   * Ejecuta el auto-fill cuando cambia el tipo de presentación (selector arriba) y hay tareas de importe completadas.
   * Debe llamarse tras disableTasks para que las tareas tengan el isDisabled correcto.
   */
  async runAutoFillWhenPresentationFormChanged(): Promise<void> {
    this.runAllAutoFills();
    if (this.changedTasks.length > 0 && this.isMultiple === 1) {
      this.isSaving = true;
      try {
        if (this.changedTasks.length > 1) {
          await this.declarationService.massTaskStatusUpdate(this.changedTasks);
        } else {
          await this.declarationService.taskStatusUpdate(this.changedTasks[0]);
        }
        this.syncInitialValues();
        this.changedTasks = [];
      } finally {
        this.isSaving = false;
      }
    }
  }

  // Formatea los valores iniciales de tipo NUMBER
  formatInitialValues() {
    if (this.phases) {
      this.phases.forEach(phase => {
        phase.tasks.forEach(task => {
          if (task.type === 'NUMBER' && (task.value || task.value === 0)) {
            task.value = this.formatNumber(task.value);
          }
        });
      });
    }
  }
}
